/**
 * Activation Screen.
 * Shows the device's MAC/ID and an input for the activation key.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDevice } from "../lib/DeviceContext";
import { PLAYER } from "../lib/testIds";
import { enableDebug } from "../lib/debugMode";
import { getDeviceMacSource } from "../lib/deviceId";
import { Copy, Loader2, AlertCircle, MessageCircle, ShieldCheck, AlertTriangle } from "lucide-react";
import "../vu-theme.css";

/**
 * Human label + colour cue for the MAC source tag produced by
 * `initDeviceId()`. The provider support team uses this to verify
 * whether the MAC the user is showing them is a real burned-in
 * hardware address or a synthesised fallback.
 */
function macSourceLabel(source) {
  if (!source) return null;
  if (source === "electron") return { text: "Real MAC (Electron IPC)", ok: true };
  if (source.startsWith("sys:")) return { text: `Real MAC (${source.replace("sys:", "")})`, ok: true };
  if (source.startsWith("ni:")) return { text: `Real MAC (${source.replace("ni:", "")} via Java NIC)`, ok: true };
  if (source === "arp") return { text: "Real MAC (ARP)", ok: true };
  if (source.startsWith("synth")) return { text: "Synthesised MAC (no hardware MAC readable)", ok: false };
  if (source === "pseudo") return { text: "Pseudo MAC (browser preview)", ok: false };
  return { text: source, ok: false };
}

export default function Activation() {
  const { deviceId, activate, panelName, supportWhatsapp } = useDevice();
  const navigate = useNavigate();
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  // v1.3.76 — show where the MAC came from so the user (and support)
  // can tell at a glance whether it's the device's real burned-in
  // address or a synthesised fallback. Only readable from
  // localStorage *after* DeviceContext has finished `initDeviceId`,
  // which is guaranteed because `useDevice` blocks until then.
  const [macSource] = useState(() => getDeviceMacSource());

  const onActivate = async (e) => {
    e?.preventDefault();
    setError("");
    const trimmed = key.trim();
    if (!trimmed) {
      setError("Please enter an activation key");
      return;
    }
    // Magic key — turns on the in-app debug console AND activates using
    // the seeded ZEROTESTING key + playlist so the user can diagnose
    // real playback / parsing issues against their actual library
    // without burning a real activation slot.
    const isLogtest = trimmed.toLowerCase() === "logtest";
    if (isLogtest) {
      enableDebug();
    }
    // `logtest` activates with the dedicated LOGTEST key (seeded in
    // backend) which has its own diagnostic playlist — keeps the
    // production ZEROTESTING fixture clean.
    const keyToActivate = isLogtest ? "LOGTEST" : trimmed.toUpperCase();
    setLoading(true);
    try {
      await activate(keyToActivate);
      // v1.4.45 — Mark first run so Intro.jsx surfaces the manual
      // screen-size adjustment prompt before the user reaches the
      // main menu. Persists across app restarts until the user
      // confirms (or dismisses) it.
      try { localStorage.setItem("tv_player.firstRunScreenSize", "1"); } catch { /* private-mode */ }
      navigate("/player/intro", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Activation failed";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const copyMac = () => {
    navigator.clipboard.writeText(deviceId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      data-testid={PLAYER.activationScreen}
      className="vu-theme min-h-screen bg-[#0b0d12] text-slate-100 flex items-center justify-center px-4 relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-orange-500/20 blur-[120px] rounded-full" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-fuchsia-500/10 blur-[120px] rounded-full" />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/za-logo-512.png"
            alt="ZA Player Pro"
            className="mx-auto w-44 h-44 sm:w-52 sm:h-52 object-contain drop-shadow-[0_0_30px_rgba(245,2,97,0.35)]"
          />
          <p className="text-slate-400 text-sm mt-1">Activate your device to continue</p>
        </div>

        {/* Player-only disclaimer */}
        <div
          data-testid="player-disclaimer"
          className="mb-5 flex gap-2.5 px-3.5 py-2.5 rounded-lg border border-amber-400/30 bg-amber-400/5 text-amber-200 text-xs leading-relaxed"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
          <span>
            This app is a <span className="font-semibold text-amber-100">player only</span> and does
            not contain any content or media.
          </span>
        </div>

        <form
          onSubmit={onActivate}
          className="bg-[#13161d]/80 backdrop-blur border border-white/5 rounded-2xl p-6 space-y-5 shadow-2xl"
        >
          {/* MAC display */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">
              Your device MAC
            </label>
            <div className="flex items-center gap-2 bg-black/40 border border-white/5 rounded-lg px-3 py-2.5">
              <span
                data-testid={PLAYER.macDisplay}
                className="font-mono text-sm flex-1 text-orange-300 select-all"
              >
                {deviceId}
              </span>
              <button
                type="button"
                data-testid={PLAYER.btnCopyMac}
                onClick={copyMac}
                className="text-slate-400 hover:text-white transition-colors p-1"
                title="Copy MAC"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              {copied ? "Copied!" : "Send this MAC to your provider together with your activation request."}
            </p>
            {/* v1.3.76 — MAC source badge. Real hardware MAC shows
                green shield; synthesised / pseudo MACs show amber
                warning so the user/provider can see at a glance. */}
            {macSource && (() => {
              const lbl = macSourceLabel(macSource);
              if (!lbl) return null;
              const Icon = lbl.ok ? ShieldCheck : AlertTriangle;
              return (
                <div
                  data-testid="mac-source-badge"
                  className={
                    "mt-2 flex items-center gap-1.5 text-[11px] " +
                    (lbl.ok ? "text-emerald-400" : "text-amber-400")
                  }
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span>{lbl.text}</span>
                </div>
              );
            })()}
          </div>

          {/* Activation key */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">
              Activation key
            </label>
            <input
              type="text"
              autoFocus
              data-testid={PLAYER.inputActivationKey}
              value={key}
              onChange={(e) => setKey(e.target.value.toUpperCase())}
              onFocus={(e) => {
                // v1.0.9 — Ensure the activation input stays visible when
                // the Android soft-keyboard slides up. windowSoftInputMode=
                // adjustResize + Capacitor Keyboard resize="body" handle
                // most cases, but a flex-center layout can leave the input
                // right behind the keyboard on some device DPIs. A one-shot
                // scrollIntoView after the keyboard animation guarantees it.
                setTimeout(() => {
                  try { e.target.scrollIntoView({ block: "center", behavior: "smooth" }); }
                  catch { /* older webviews — noop */ }
                }, 300);
              }}
              placeholder="XXXXXXXXXXXX"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 font-mono tracking-widest text-center text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-orange-500/60 focus:ring-1 focus:ring-orange-500/40"
              maxLength={14}
            />
          </div>

          {error && (
            <div className="text-sm bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid={PLAYER.btnActivate}
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-400 hover:to-pink-400 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-lg py-2.5 transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Activating…
              </>
            ) : (
              "Activate device"
            )}
          </button>

          <p className="text-center text-[11px] text-slate-500">
            Don&apos;t have a key? Ask your provider — or request a 5-day trial,
            or go to{" "}
            <a
              href="https://zaplayerpro.club"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 underline hover:text-emerald-300"
              data-testid="activation-visit-website-link"
            >
              zaplayerpro.club
            </a>
            .
          </p>
        </form>
      </div>

      {/* WhatsApp support floating button (bottom-right) */}
      {supportWhatsapp && (
        <a
          data-testid="player-whatsapp-btn"
          href={`https://wa.me/${supportWhatsapp.replace(/\D/g, "")}`}
          target="_blank"
          rel="noopener noreferrer"
          title={`WhatsApp ${supportWhatsapp}`}
          className="fixed bottom-5 right-5 z-30 flex items-center gap-2 bg-[#25D366] hover:bg-[#1DBE56] text-white font-medium text-sm rounded-full pl-3 pr-4 py-2.5 shadow-2xl shadow-emerald-900/40 transition-transform hover:scale-105 active:scale-95"
        >
          <MessageCircle className="w-4 h-4 fill-white" />
          <span className="hidden sm:inline">Need help?</span>
          <span className="font-mono text-xs opacity-90 hidden md:inline">
            {supportWhatsapp}
          </span>
        </a>
      )}
    </div>
  );
}
